import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import { graphql } from 'react-apollo';
import { withRouter } from 'react-router-dom';
import { Message } from 'semantic-ui-react';
import { isEmpty } from 'lodash';
import { connect } from 'react-redux';

import LoadingDataEnhancer from './LoadingData';
import withPagination from './WithPagination';
import GuaranteePurchasesList from '../views/GuaranteePurchases/List';
import Search from '../views/Shared/Search';
import GuaranteePurchaseEdit from '../views/GuaranteePurchases/EditForm';
import GuaranteePurchaseExtend from '../views/GuaranteePurchases/ExtendForm';
import GuaranteePurchaseCancel from '../views/GuaranteePurchases/CancelForm';
import GuaranteePurchaseAudit from '../views/GuaranteePurchases/Audit';

import Pagination from '../views/Shared/Pagination';
import useMockGuarantees from '../hooks/useMockGuarantees';
import { useGuaranteeProduct } from '../hooks/useGuaranteePurchases';
import guaranteePurchasesQuery from '../graphql/guaranteePurchasesQuery';
import updateGuaranteePurchaseMutation from '../graphql/updateGuaranteePurchaseMutation';
import cancelGuaranteePurchaseMutation from '../graphql/cancelGuaranteePurchaseMutation';
import { clearSearch, setSearch } from '../data/searchField/actions';
import { setRefreshSearch } from '../data/refreshSearch/actions';
import APP_PATH from '../constants/paths';
import { setPage } from '../data/page/actions';
import { GUARANTEES_V2_ENABLED } from '../config';

const DEFAULT_PAGE_SIZE = 25;

// Mounted only when GUARANTEES_V2_ENABLED is true, so the hook and mock data
// are never instantiated in production.
const MockGuaranteePurchases = () => {
  const [selectedProduct] = useGuaranteeProduct();
  const [mockPage, setMockPage] = useState(1);
  const [localSearchFields, setLocalSearchFields] = useState({});

  const {
    data: guaranteePurchases,
    loading,
    isLastPage,
    start,
    end,
  } = useMockGuarantees({
    productFamily: selectedProduct || '',
    tab: 'guarantee',
    page: mockPage,
    searchFields: localSearchFields,
  });

  const [selectedGuaranteePurchase, setSelectedGuaranteePurchase] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAuditModalOpen, setIsAuditModalOpen] = useState(false);
  const [editFormError, setEditFormError] = useState(null);
  const [editFormSubmitting, setEditFormSubmitting] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [cancelFormError, setCancelFormError] = useState(null);
  const [cancelFormSubmitting, setCancelFormSubmitting] = useState(false);
  const [flashMessage, setFlashMessage] = useState(null);
  const [isExtendModalOpen, setIsExtendModalOpen] = useState(false);
  const [extendFormError, setExtendFormError] = useState(null);
  const [extendFormSubmitting, setExtendFormSubmitting] = useState(false);

  const handleMessageDismiss = useCallback(() => {
    setFlashMessage(null);
  }, []);

  const onEditFormCloseHandler = useCallback(() => {
    setIsEditModalOpen(false);
    setSelectedGuaranteePurchase(null);
    setEditFormError(null);
    setEditFormSubmitting(null);
  }, []);

  const onAuditCloseHandler = useCallback(() => {
    setIsAuditModalOpen(false);
    setSelectedGuaranteePurchase(null);
  }, []);

  const onExtendFormCloseHandler = useCallback(() => {
    setIsExtendModalOpen(false);
    setSelectedGuaranteePurchase(null);
    setExtendFormError(null);
    setExtendFormSubmitting(null);
  }, []);

  const onCancelFormCloseHandler = useCallback(() => {
    setIsCancelModalOpen(false);
    setSelectedGuaranteePurchase(null);
  }, []);

  const onSelectGuarantee = useCallback((action, guarantee) => {
    setSelectedGuaranteePurchase(guarantee);

    switch (action) {
      case 'Edit':
        setIsEditModalOpen(true);
        setIsCancelModalOpen(false);
        break;
      case 'Extend Guarantee':
        setIsExtendModalOpen(true);
        setExtendFormError(null);
        setExtendFormSubmitting(null);
        setIsCancelModalOpen(false);
        setIsEditModalOpen(false);
        break;
      case 'Cancel Guarantee':
        setIsCancelModalOpen(true);
        setIsEditModalOpen(false);
        setCancelFormError(null);
        setCancelFormSubmitting(null);
        break;
      case 'Audit':
        setIsAuditModalOpen(true);
        setIsEditModalOpen(false);
        setCancelFormError(null);
        setCancelFormSubmitting(null);
        break;
      default:
        setSelectedGuaranteePurchase(null);
    }
  }, []);

  const onEditFormSubmitHandler = useCallback(() => {
    setEditFormSubmitting(false);
    setFlashMessage('Successfully Updated Guarantee Purchase');
    onEditFormCloseHandler();
  }, [onEditFormCloseHandler]);

  const onExtendFormSubmitHandler = useCallback(() => {
    setExtendFormSubmitting(false);
    setFlashMessage('Successfully extended Guarantee Purchase');
    onExtendFormCloseHandler();
  }, [onExtendFormCloseHandler]);

  const onCancelFormSubmitHandler = useCallback(() => {
    setCancelFormSubmitting(false);
    setIsCancelModalOpen(false);
    setSelectedGuaranteePurchase(null);
    setFlashMessage('Successfully Cancelled Guarantee Purchase');
  }, []);

  const onSearchSubmitHandler = useCallback(event => {
    event.preventDefault();
    const { vin, dealerNumbers } = event.target;
    const dealerNos = dealerNumbers && dealerNumbers.value ? [dealerNumbers.value.trim()] : [];
    const fields = {
      vin: vin ? vin.value.trim() || null : null,
      dealerNumbers: dealerNos,
    };
    setLocalSearchFields(fields);
    setMockPage(1);
  }, []);

  const nextHandler = useCallback(() => {
    if (!isLastPage) setMockPage(p => p + 1);
  }, [isLastPage]);

  const previousHandler = useCallback(() => {
    setMockPage(p => Math.max(1, p - 1));
  }, []);

  return (
    <div>
      {flashMessage && (
        <Message
          onDismiss={handleMessageDismiss}
          content={flashMessage}
          positive
        />
      )}
      <Search
        onSubmit={onSearchSubmitHandler}
        searchFields={{}}
        searchOptions={['vin', 'dealerNumbers']}
      />
      <GuaranteePurchasesList
        onSelectGuarantee={onSelectGuarantee}
        guaranteePurchases={guaranteePurchases}
      />
      <Pagination
        start={start}
        end={end}
        nextHandler={nextHandler}
        previousHandler={previousHandler}
        loading={loading}
        lastPage={isLastPage}
        pageSize={DEFAULT_PAGE_SIZE}
      />

      {selectedGuaranteePurchase && isEditModalOpen && (
        <div>
          <GuaranteePurchaseEdit
            isModalOpen={isEditModalOpen}
            onCloseHandler={onEditFormCloseHandler}
            onSubmitHandler={onEditFormSubmitHandler}
            guaranteePurchase={selectedGuaranteePurchase}
            error={editFormError}
            submitting={editFormSubmitting}
          />
        </div>
      )}

      {selectedGuaranteePurchase && isCancelModalOpen && (
        <div>
          <GuaranteePurchaseCancel
            isCancelModalOpen={isCancelModalOpen}
            onCloseHandler={onCancelFormCloseHandler}
            onSubmitHandler={onCancelFormSubmitHandler}
            guaranteePurchase={selectedGuaranteePurchase}
            error={cancelFormError}
            submitting={cancelFormSubmitting}
          />
        </div>
      )}

      {selectedGuaranteePurchase && isAuditModalOpen && (
        <div>
          <GuaranteePurchaseAudit
            isAuditModalOpen={isAuditModalOpen}
            onCloseHandler={onAuditCloseHandler}
            guaranteePurchase={selectedGuaranteePurchase}
          />
        </div>
      )}

      {selectedGuaranteePurchase && isExtendModalOpen && (
        <div>
          <GuaranteePurchaseExtend
            isModalOpen={isExtendModalOpen}
            onCloseHandler={onExtendFormCloseHandler}
            onSubmitHandler={onExtendFormSubmitHandler}
            guaranteePurchase={selectedGuaranteePurchase}
            error={extendFormError}
            submitting={extendFormSubmitting}
          />
        </div>
      )}
    </div>
  );
};

const GuaranteePurchases = props => {
  if (GUARANTEES_V2_ENABLED) {
    return <MockGuaranteePurchases />;
  }

  const {
    data: { guaranteePurchases, refetch },
    searchFields,
    refreshSearch,
    clearSearchFields,
    dispatchRefreshSearch,
    setSearchFields: setSearchFieldsAction,
    setPage: setPageAction,
    updateMutation,
    cancelMutation,
    history,
  } = props;

  return (
    <LegacyGuaranteePurchases
      guaranteePurchases={guaranteePurchases}
      searchFields={searchFields}
      refreshSearch={refreshSearch}
      clearSearchFields={clearSearchFields}
      dispatchRefreshSearch={dispatchRefreshSearch}
      setSearchFieldsAction={setSearchFieldsAction}
      setPageAction={setPageAction}
      updateMutation={updateMutation}
      cancelMutation={cancelMutation}
      history={history}
      refetch={refetch}
    />
  );
};

const LegacyGuaranteePurchases = ({
  guaranteePurchases,
  searchFields,
  refreshSearch,
  clearSearchFields,
  dispatchRefreshSearch,
  setSearchFieldsAction,
  setPageAction,
  updateMutation,
  cancelMutation,
  history,
  refetch,
}) => {
  const [selectedGuaranteePurchase, setSelectedGuaranteePurchase] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAuditModalOpen, setIsAuditModalOpen] = useState(false);
  const [editFormError, setEditFormError] = useState(null);
  const [editFormSubmitting, setEditFormSubmitting] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [cancelFormError, setCancelFormError] = useState(null);
  const [cancelFormSubmitting, setCancelFormSubmitting] = useState(false);
  const [flashMessage, setFlashMessage] = useState(null);
  const [isExtendModalOpen, setIsExtendModalOpen] = useState(false);
  const [extendFormError, setExtendFormError] = useState(null);
  const [extendFormSubmitting, setExtendFormSubmitting] = useState(false);

  useEffect(() => {
    if (refreshSearch) {
      dispatchRefreshSearch(false);
      clearSearchFields({});
      const variableArguments = {
        vin: null,
        dealerNumbers: [],
      };
      refetch({
        ...variableArguments,
      });
    }
  }, [refreshSearch, dispatchRefreshSearch, clearSearchFields, refetch]);

  const handleMessageDismiss = useCallback(() => {
    setFlashMessage(null);
  }, []);

  const onEditFormCloseHandler = useCallback(() => {
    setIsEditModalOpen(false);
    setSelectedGuaranteePurchase(null);
    setEditFormError(null);
    setEditFormSubmitting(null);
  }, []);

  const onAuditCloseHandler = useCallback(() => {
    setIsAuditModalOpen(false);
    setSelectedGuaranteePurchase(null);
  }, []);

  const onExtendFormCloseHandler = useCallback(() => {
    setIsExtendModalOpen(false);
    setSelectedGuaranteePurchase(null);
    setExtendFormError(null);
    setExtendFormSubmitting(null);
  }, []);

  const onCancelFormCloseHandler = useCallback(() => {
    setIsCancelModalOpen(false);
    setSelectedGuaranteePurchase(null);
  }, []);

  const onSelectGuarantee = useCallback(
    (action, guarantee) => {
      setSelectedGuaranteePurchase(guarantee);

      switch (action) {
        case 'Edit':
          setIsEditModalOpen(true);
          setIsCancelModalOpen(false);
          break;
        case 'Extend Guarantee':
          setIsExtendModalOpen(true);
          setExtendFormError(null);
          setExtendFormSubmitting(null);
          setIsCancelModalOpen(false);
          setIsEditModalOpen(false);
          break;
        case 'Cancel Guarantee':
          setIsCancelModalOpen(true);
          setIsEditModalOpen(false);
          setCancelFormError(null);
          setCancelFormSubmitting(null);
          break;
        case 'Audit':
          setIsAuditModalOpen(true);
          setIsEditModalOpen(false);
          setCancelFormError(null);
          setCancelFormSubmitting(null);
          break;
        case 'Vehicle Status Tracker':
          history.push(
            APP_PATH.VEHICLE_STATUS_TRACKER.replace(
              ':purchaseId',
              guarantee.id
            )
          );
          break;
        default:
          setSelectedGuaranteePurchase(null);
      }
    },
    [history]
  );

  const onEditFormSubmitHandler = useCallback(
    values => {
      const purchaseInput = { guaranteePurchase: { ...values } };
      setEditFormSubmitting(true);
      updateMutation({
        variables: { purchaseInput },
      })
        .then(({ data: mutationData }) => {
          const {
            updateGuaranteePurchase: { success, errors },
          } = mutationData;
          if (success) {
            setEditFormSubmitting(false);
            setFlashMessage('Successfully Updated Guarantee Purchase');
            refetch();
            setPageAction({});
            onEditFormCloseHandler();
          } else {
            setEditFormSubmitting(false);
            setEditFormError(errors);
          }
        })
        .catch(() => {
          setEditFormSubmitting(false);
          setEditFormError('Error while updating Guarantee Purchase');
        });
    },
    [updateMutation, refetch, setPageAction, onEditFormCloseHandler]
  );

  const onExtendFormSubmitHandler = useCallback(
    values => {
      const purchaseInput = { guaranteePurchase: { ...values } };
      setExtendFormSubmitting(true);
      updateMutation({
        variables: { purchaseInput },
      })
        .then(({ data: mutationData }) => {
          const {
            updateGuaranteePurchase: { success, errors },
          } = mutationData;
          if (success) {
            setExtendFormSubmitting(false);
            setFlashMessage('Successfully extended Guarantee Purchase');
            onExtendFormCloseHandler();
          } else {
            setExtendFormSubmitting(false);
            setExtendFormError(errors);
          }
        })
        .catch(() => {
          setExtendFormSubmitting(false);
          setExtendFormError('Error while extending Guarantee Purchase');
        });
    },
    [updateMutation, onExtendFormCloseHandler]
  );

  const onCancelFormSubmitHandler = useCallback(
    values => {
      setCancelFormSubmitting(true);
      const cancelInput = { ...values };
      cancelMutation({
        variables: { cancelInput },
      })
        .then(({ data: mutationData }) => {
          const {
            cancelGuaranteePurchase: { success, errors },
          } = mutationData;
          if (success) {
            refetch();
            setIsCancelModalOpen(false);
            setSelectedGuaranteePurchase(null);
            setCancelFormSubmitting(null);
            setFlashMessage('Successfully Cancelled Guarantee Purchase');
          } else {
            setCancelFormSubmitting(false);
            setCancelFormError(errors);
          }
        })
        .catch(() => {
          setCancelFormSubmitting(false);
          setCancelFormError('Error while cancelling Guarantee Purchase');
        });
    },
    [cancelMutation, refetch]
  );

  const onSearchSubmitHandler = useCallback(
    event => {
      const { vin, dealerNumbers } = event.target;
      const dealerNos = isEmpty(dealerNumbers.value)
        ? null
        : [dealerNumbers.value.trim()];
      const variableArguments = {
        vin: vin.value.trim() || null,
        dealerNumbers: dealerNos,
      };
      refetch({
        ...variableArguments,
      });

      setSearchFieldsAction(variableArguments);
      setPageAction({});
    },
    [refetch, setSearchFieldsAction, setPageAction]
  );

  return (
    <div>
      {flashMessage && (
        <Message
          onDismiss={handleMessageDismiss}
          content={flashMessage}
          positive
        />
      )}
      <Search
        onSubmit={onSearchSubmitHandler}
        searchFields={searchFields}
        searchOptions={['vin', 'dealerNumbers']}
      />
      <GuaranteePurchasesList
        onSelectGuarantee={onSelectGuarantee}
        guaranteePurchases={guaranteePurchases}
      />

      {selectedGuaranteePurchase && isEditModalOpen && (
        <div>
          <GuaranteePurchaseEdit
            isModalOpen={isEditModalOpen}
            onCloseHandler={onEditFormCloseHandler}
            onSubmitHandler={onEditFormSubmitHandler}
            guaranteePurchase={selectedGuaranteePurchase}
            error={editFormError}
            submitting={editFormSubmitting}
          />
        </div>
      )}

      {selectedGuaranteePurchase && isCancelModalOpen && (
        <div>
          <GuaranteePurchaseCancel
            isCancelModalOpen={isCancelModalOpen}
            onCloseHandler={onCancelFormCloseHandler}
            onSubmitHandler={onCancelFormSubmitHandler}
            guaranteePurchase={selectedGuaranteePurchase}
            error={cancelFormError}
            submitting={cancelFormSubmitting}
          />
        </div>
      )}

      {selectedGuaranteePurchase && isAuditModalOpen && (
        <div>
          <GuaranteePurchaseAudit
            isAuditModalOpen={isAuditModalOpen}
            onCloseHandler={onAuditCloseHandler}
            guaranteePurchase={selectedGuaranteePurchase}
          />
        </div>
      )}

      {selectedGuaranteePurchase && isExtendModalOpen && (
        <div>
          <GuaranteePurchaseExtend
            isModalOpen={isExtendModalOpen}
            onCloseHandler={onExtendFormCloseHandler}
            onSubmitHandler={onExtendFormSubmitHandler}
            guaranteePurchase={selectedGuaranteePurchase}
            error={extendFormError}
            submitting={extendFormSubmitting}
          />
        </div>
      )}
    </div>
  );
};

LegacyGuaranteePurchases.propTypes = {
  guaranteePurchases: PropTypes.array,
  searchFields: PropTypes.shape({
    vin: PropTypes.string,
    dealerNumbers: PropTypes.array,
  }).isRequired,
  refreshSearch: PropTypes.bool,
  clearSearchFields: PropTypes.func.isRequired,
  dispatchRefreshSearch: PropTypes.func.isRequired,
  setSearchFieldsAction: PropTypes.func.isRequired,
  setPageAction: PropTypes.func.isRequired,
  updateMutation: PropTypes.func,
  cancelMutation: PropTypes.func,
  history: PropTypes.shape({
    push: PropTypes.func,
  }).isRequired,
  refetch: PropTypes.func.isRequired,
};

LegacyGuaranteePurchases.defaultProps = {
  guaranteePurchases: [],
  refreshSearch: false,
  updateMutation: null,
  cancelMutation: null,
};

GuaranteePurchases.propTypes = {
  data: PropTypes.shape({
    guaranteePurchases: PropTypes.array,
    refetch: PropTypes.func,
  }),
  searchFields: PropTypes.shape({
    vin: PropTypes.string,
    dealerNumbers: PropTypes.array,
  }).isRequired,
  refreshSearch: PropTypes.bool,
  clearSearchFields: PropTypes.func.isRequired,
  dispatchRefreshSearch: PropTypes.func.isRequired,
  setSearchFields: PropTypes.func.isRequired,
  setPage: PropTypes.func.isRequired,
  updateMutation: PropTypes.func,
  cancelMutation: PropTypes.func,
  history: PropTypes.shape({
    push: PropTypes.func,
  }).isRequired,
};

GuaranteePurchases.defaultProps = {
  data: {},
  refreshSearch: false,
  updateMutation: null,
  cancelMutation: null,
};

const mapDispatchToProps = dispatch => ({
  setSearchFields: fields => {
    dispatch(setSearch(fields));
  },
  dispatchRefreshSearch: value => {
    dispatch(setRefreshSearch(value));
  },
  clearSearchFields: () => {
    dispatch(clearSearch());
  },
  setPage: () => {
    dispatch(setPage());
  },
});

const mapStateToProps = state => {
  const {
    data: { searchFields, refreshSearch },
  } = state;
  return {
    searchFields,
    refreshSearch,
  };
};

const legacyHOCs = GUARANTEES_V2_ENABLED
  ? []
  : [
    withPagination(guaranteePurchasesQuery, 'guaranteePurchases'),
    graphql(updateGuaranteePurchaseMutation, { name: 'updateMutation' }),
    graphql(cancelGuaranteePurchaseMutation, { name: 'cancelMutation' }),
    LoadingDataEnhancer,
  ];

export default compose(
  withRouter,
  ...legacyHOCs,
  connect(mapStateToProps, mapDispatchToProps)
)(GuaranteePurchases);
